import { useState, type FC } from 'react';
import { LuExternalLink, LuQuote, LuArrowRight, LuCheck, LuTriangleAlert, LuEyeOff, LuTrophy } from 'react-icons/lu';
import AssistenteService, { type AchadoFunil, type AcaoAssistente } from '../services/AssistenteService';
import aviso, { textoDoErro } from '../lib/aviso';
import './AssistenteAchado.css';

const moeda = (valor: number) =>
    valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** "1.234,56" e "1234.56" viram 1234.56; o que não é número vira nulo. */
const lerValor = (texto: string): number | null => {
    const limpo = texto.replace(/[^\d,.-]/g, '').replace(/\.(?=\d{3}\b)/g, '').replace(',', '.');
    const numero = Number(limpo);
    return limpo !== '' && Number.isFinite(numero) && numero > 0 ? numero : null;
};

/** O que o selo do topo diz, por tipo de achado. */
const SELO: Record<AchadoFunil['tipo'], string> = {
    ganha: 'A conversa diz que fechou',
    provavel: 'Parece fechada',
    etapa: 'Está atrás no funil',
    fora_de_venda: 'Não parece ser um negócio',
};

/**
 * Uma coisa que o Brik AI leu nas conversas e aponta: venda que já fechou,
 * etapa que devia ter avançado ou conversa que não é negócio (21/10/2026).
 *
 * É primo do cartão de ação (`agt-ac`) e tem uma diferença que manda no
 * desenho: **a varredura não fez nada**. Não há confirmar nem desfazer — há a
 * frase da conversa que prova e o caminho para a pessoa decidir.
 *
 * O cartão de **etapa** move e o de **venda** fecha a venda — os dois executam
 * no clique, com desfazer. O de venda ganhou botão em 25/10, depois de duas
 * semanas medindo: 42 apontamentos de venda não viraram ação nenhuma por falta
 * de botão, e mesmo assim 22 das 23 oportunidades apontadas acabaram em
 * "Ganho", fechadas uma a uma na mão. **Perda continua fora**: o modelo erra a
 * perda (5 de 6) e ele decidiu em 25/10 que não vamos fazer.
 *
 * O "Abrir oportunidade" vai em **nova aba** de propósito (pedido dele em
 * 21/10): a lista costuma ter vários itens e sair da tela perderia o resto. No
 * celular não há nova aba que ajude, então ali abre na mesma.
 */
const AssistenteAchado: FC<{
    achado: AchadoFunil;
    celular?: boolean;
    /**
     * Chamado quando o botão do cartão vira uma ação — ela entra na conversa
     * com o desfazer. Leva o id do achado junto: é por ele que a barra do lote
     * sabe que este cartão já foi resolvido sozinho.
     */
    onMoveu?: (acao: AcaoAssistente, achadoId: number) => void;
    /** Marcado para o lote; undefined quando o cartão não participa de seleção. */
    selecionado?: boolean;
    onSelecionar?: (marcado: boolean) => void;
    /** Movida pelo lote: o cartão mostra o feito sem ter sido clicado. */
    movidaEmLote?: boolean;
    /**
     * O valor da venda, quando a pessoa corrigiu o que o Brik leu. Mora no pai
     * porque o lote precisa dele: marcar o cartão e fechar pela barra tem de
     * usar o valor corrigido, não o original.
     */
    valor?: number | null;
    onValor?: (valor: number | null) => void;
}> = ({ achado, celular = false, onMoveu, selecionado, onSelecionar, movidaEmLote, valor, onValor }) => {
    const [estado, setEstado] = useState<'pronto' | 'movendo' | 'movido'>('pronto');
    const feito = estado === 'movido' || !!movidaEmLote;

    /* O texto cru de enquanto se digita: "27," não é número ainda, e converter
       a cada tecla apagaria a vírgula na frente da pessoa.

       `valor` já vem resolvido pelo pai (digitado, apagado ou o do Brik): não
       cair no `achado.valorSugerido` aqui é o que deixa o campo ficar vazio
       quando a pessoa apaga. */
    const valorAtual = valor ?? null;
    const [digitado, setDigitado] = useState<string | null>(null);
    const noCampo = digitado ?? (valorAtual === null ? '' : String(valorAtual).replace('.', ','));

    const agir = async (
        chamada: (achadoId: number) => Promise<AcaoAssistente>,
        falha: string,
    ) => {
        setEstado('movendo');
        try {
            const acao = await chamada(achado.achadoId);
            setEstado('movido');
            onMoveu?.(acao, achado.achadoId);
        } catch (erro) {
            setEstado('pronto');
            aviso.erro(textoDoErro(erro, falha));
        }
    };

    const podeMover = achado.tipo === 'etapa' && !!achado.etapaSugerida && achado.achadoId > 0;

    /* Tirar do funil é a ação do cartão "não parece ser um negócio": o contato
       sai da Pipeline e das métricas, e a conversa fica no Chat. */
    const podeTirarDoFunil = achado.tipo === 'fora_de_venda' && achado.achadoId > 0;

    /* "Pode estar ganha" fecha pelo mesmo botão: a frase que prova está no
       cartão, e quem decide é quem está lendo. */
    const ehVenda = achado.tipo === 'ganha' || achado.tipo === 'provavel';
    const podeGanhar = ehVenda && achado.achadoId > 0;

    return (
        <div className={`agt-ah is-${achado.tipo}`}>
            <div className="agt-ah-k">
                <span>{SELO[achado.tipo]}</span>

                {/* A caixinha fica à DIREITA: à esquerda do selo ela empurrava o
                    texto 18px e os cartões com e sem seleção desalinhavam. */}
                {onSelecionar && !feito && (
                    <label className="agt-ah-sel">
                        <input
                            type="checkbox"
                            checked={!!selecionado}
                            onChange={e => onSelecionar(e.target.checked)}
                        />
                    </label>
                )}
            </div>

            <div className="agt-ah-t">
                {achado.contato}
                <span className="agt-ah-num">nº {achado.oportunidadeId}</span>
            </div>

            <div className="agt-ah-m">
                <span className="agt-ah-etapa">{achado.etapa}</span>
                {achado.etapaSugerida && (
                    <>
                        <LuArrowRight size={12} className="agt-ah-seta" />
                        <b>{achado.etapaSugerida}</b>
                    </>
                )}
                {achado.responsavel && <span className="agt-ah-sep">·</span>}
                {achado.responsavel && <span>{achado.responsavel}</span>}
            </div>

            <div className="agt-ah-prova">
                <LuQuote size={13} className="agt-ah-aspas" />
                <span>{achado.prova}</span>
            </div>

            {/* No cartão de venda o valor é editável: é ele que vai para o
                faturamento, e a conta da conversa costuma faltar o frete. Nos
                outros cartões continua sendo só leitura. */}
            {podeGanhar && !feito ? (
                <div className="agt-ah-valor">
                    <label className="agt-ah-r" htmlFor={`agt-val-${achado.achadoId}`}>Valor da venda</label>
                    <span className="agt-ah-campo">
                        <span className="agt-ah-cifra">R$</span>
                        <input
                            id={`agt-val-${achado.achadoId}`}
                            type="text"
                            inputMode="decimal"
                            value={noCampo}
                            /* Sem placeholder de número: "0,00" em cinza se lê
                               como venda de zero real, e não como campo vazio. */
                            placeholder=""
                            onChange={e => {
                                setDigitado(e.target.value);
                                onValor?.(lerValor(e.target.value));
                            }}
                            onBlur={() => setDigitado(null)}
                        />
                    </span>
                    {achado.contaDoValor && <span className="agt-ah-conta">{achado.contaDoValor}</span>}
                </div>
            ) : typeof achado.valorSugerido === 'number' && achado.valorSugerido > 0 && (
                <div className="agt-ah-valor">
                    <span className="agt-ah-r">Valor na conversa</span>
                    <b>{moeda(achado.valorSugerido)}</b>
                    {achado.contaDoValor && <span className="agt-ah-conta">{achado.contaDoValor}</span>}
                </div>
            )}

            {podeGanhar && !feito && lerValor(noCampo) === null && (
                <div className="agt-ah-nota">
                    Informe o valor para dar como ganho — é ele que entra no seu faturamento.
                </div>
            )}

            {achado.tipo === 'fora_de_venda' && !feito && (
                <div className="agt-ah-nota">
                    Fora do funil, a conversa continua no Chat e deixa de contar nos números da empresa.
                </div>
            )}

            <div className="agt-ah-f">
                {podeMover && !feito && (
                    <button
                        type="button"
                        className="agt-ah-b is-prim"
                        disabled={estado === 'movendo'}
                        onClick={() => agir(AssistenteService.moverPeloAchado, 'Não foi possível mover a oportunidade.')}
                    >
                        <LuArrowRight size={14} /> Mover para {achado.etapaSugerida}
                    </button>
                )}

                {podeTirarDoFunil && !feito && (
                    <button
                        type="button"
                        className="agt-ah-b"
                        disabled={estado === 'movendo'}
                        onClick={() => agir(AssistenteService.tirarDoFunilPeloAchado, 'Não foi possível tirar do funil.')}
                    >
                        <LuEyeOff size={14} /> Tirar do funil
                    </button>
                )}

                {podeGanhar && !feito && (
                    <button
                        type="button"
                        className="agt-ah-b is-prim"
                        disabled={estado === 'movendo' || lerValor(noCampo) === null}
                        onClick={() => agir(
                            id => AssistenteService.ganharPeloAchado(id, lerValor(noCampo)),
                            'Não foi possível dar a venda como ganha.',
                        )}
                    >
                        <LuTrophy size={14} /> Dar como ganho
                    </button>
                )}

                {feito && (
                    <span className="agt-ah-feito">
                        <LuCheck size={14} />
                        {achado.tipo === 'fora_de_venda'
                            ? 'Fora do funil'
                            : ehVenda
                                ? `Venda de ${moeda(valorAtual ?? 0)} registrada`
                                : `Movida para ${achado.etapaSugerida}`}
                    </span>
                )}

                <a
                    className="agt-ah-b"
                    href={achado.rota}
                    target={celular ? undefined : '_blank'}
                    rel={celular ? undefined : 'noreferrer'}
                >
                    <LuExternalLink size={14} /> Abrir oportunidade
                </a>
            </div>

            {(podeMover || ehVenda) && achado.achadoId <= 0 && (
                <div className="agt-ah-nota">
                    <LuTriangleAlert size={13} /> Dá para resolver pela própria oportunidade.
                </div>
            )}
        </div>
    );
};

export default AssistenteAchado;
